import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import databaseAPI from '../api/databaseAPI';
import '../styles/SalesReport.css';

/** Today as YYYY-MM-DD in Asia/Kolkata (matches report API tz) */
function todayIST() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Pure calendar add/subtract on YYYY-MM-DD */
function addDaysYmd(ymd, deltaDays) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Pure calendar add/subtract months on YYYY-MM-DD */
function addMonthsYmd(ymd, deltaMonths) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCMonth(dt.getUTCMonth() + deltaMonths);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function getRangeForPreset(preset) {
  const today = todayIST();
  switch (preset) {
    case 'all':
      return { dateFrom: '2000-01-01', dateTo: today };
    case '1d':
      return { dateFrom: today, dateTo: today };
    case 'yesterday': {
      const y = addDaysYmd(today, -1);
      return { dateFrom: y, dateTo: y };
    }
    case '3d':
      return { dateFrom: addDaysYmd(today, -2), dateTo: today };
    case '7d':
      return { dateFrom: addDaysYmd(today, -6), dateTo: today };
    case '15d':
      return { dateFrom: addDaysYmd(today, -14), dateTo: today };
    case '30d':
      return { dateFrom: addDaysYmd(today, -29), dateTo: today };
    case '3m':
      return { dateFrom: addMonthsYmd(today, -3), dateTo: today };
    case '1y':
      return { dateFrom: addDaysYmd(today, -364), dateTo: today };
    default:
      return null;
  }
}

/** Inclusive calendar days between two YYYY-MM-DD strings */
function inclusiveDayCount(fromStr, toStr) {
  const d0 = new Date(`${fromStr}T12:00:00`);
  const d1 = new Date(`${toStr}T12:00:00`);
  if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) return null;
  const diff = Math.round((d1.getTime() - d0.getTime()) / 86400000);
  return diff >= 0 ? diff + 1 : null;
}

function formatShortDate(yyyyMmDd) {
  const d = new Date(`${yyyyMmDd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return yyyyMmDd;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const LIST_MAX = 400;
const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: '1d', label: '1 Day' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '3d', label: 'Last 3 Days' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '15d', label: 'Last 15 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '1y', label: 'Last 1 Year' },
  { value: 'custom', label: 'Custom Range' },
];

const SalesReport = () => {
  const [orderScope, setOrderScope] = useState('shipping'); // all | shipping | digital
  const [datePreset, setDatePreset] = useState('30d');
  const [{ dateFrom, dateTo }, setRange] = useState(() => getRangeForPreset('30d'));
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [productList, setProductList] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('total_desc');
  const [activeTab, setActiveTab] = useState('product');
  const [daySortBy, setDaySortBy] = useState('date_desc');
  const [dayStatusFilter, setDayStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ rows: [], daily: [], totals: {} });
  const [listOpen, setListOpen] = useState(false);
  const comboboxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProductsLoading(true);
      const res = await databaseAPI.getProductsList();
      if (!cancelled && res.success) {
        setProductList(res.products || []);
      }
      if (!cancelled) setProductsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return productList;
    return productList.filter((p) => p.title.toLowerCase().includes(q));
  }, [productList, search]);

  const listSlice = useMemo(
    () => filteredProducts.slice(0, LIST_MAX),
    [filteredProducts],
  );

  const rangeHint = useMemo(() => {
    const n = inclusiveDayCount(dateFrom, dateTo);
    if (n == null) return null;
    return {
      days: n,
      fromLabel: formatShortDate(dateFrom),
      toLabel: formatShortDate(dateTo),
    };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const onDoc = (e) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target)) {
        setListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await databaseAPI.getBookSalesReport({
      dateFrom,
      dateTo,
      search: appliedSearch,
      includeDaily: true,
      orderScope,
    });
    if (!res.success) {
      setError(res.error || 'Report failed');
      setData({ rows: [], daily: [], totals: {} });
    } else {
      setData({
        rows: res.rows || [],
        daily: res.daily || [],
        totals: res.totals || {},
      });
    }
    setLoading(false);
  }, [dateFrom, dateTo, appliedSearch, orderScope]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = () => {
    setAppliedSearch(search.trim());
  };

  const handlePresetChange = (e) => {
    const v = e.target.value;
    setDatePreset(v);
    if (v !== 'custom') {
      const r = getRangeForPreset(v);
      if (r) setRange(r);
    }
  };

  const handleCustomFromChange = (e) => {
    setDatePreset('custom');
    setRange((r) => ({ ...r, dateFrom: e.target.value }));
  };

  const handleCustomToChange = (e) => {
    setDatePreset('custom');
    setRange((r) => ({ ...r, dateTo: e.target.value }));
  };

  const pickProduct = (title) => {
    if (title === null || title === '') {
      setSearch('');
      setAppliedSearch('');
    } else {
      setSearch(title);
      setAppliedSearch(title.trim());
    }
    setListOpen(false);
  };

  const t = data.totals || {};

  const sortedRows = useMemo(() => {
    const rows = Array.isArray(data.rows) ? [...data.rows] : [];
    const toReleaseTs = (r) => {
      if (!r?.book_released_at) return null;
      const dt = new Date(`${r.book_released_at}T00:00:00Z`);
      const ts = dt.getTime();
      return Number.isNaN(ts) ? null : ts;
    };
    const byTitle = (a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), undefined, { sensitivity: 'base' });

    rows.sort((a, b) => {
      switch (sortBy) {
        case 'release_desc': {
          const ta = toReleaseTs(a);
          const tb = toReleaseTs(b);
          if (ta == null && tb == null) return byTitle(a, b);
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta || byTitle(a, b);
        }
        case 'release_asc': {
          const ta = toReleaseTs(a);
          const tb = toReleaseTs(b);
          if (ta == null && tb == null) return byTitle(a, b);
          if (ta == null) return 1;
          if (tb == null) return -1;
          return ta - tb || byTitle(a, b);
        }
        case 'total_asc':
          return (a.total_quantity ?? 0) - (b.total_quantity ?? 0) || byTitle(a, b);
        case 'preorder_desc':
          return (b.preorder_quantity ?? 0) - (a.preorder_quantity ?? 0) || (b.total_quantity ?? 0) - (a.total_quantity ?? 0) || byTitle(a, b);
        case 'preorder_asc':
          return (a.preorder_quantity ?? 0) - (b.preorder_quantity ?? 0) || (a.total_quantity ?? 0) - (b.total_quantity ?? 0) || byTitle(a, b);
        case 'regular_desc':
          return (b.regular_quantity ?? 0) - (a.regular_quantity ?? 0) || (b.total_quantity ?? 0) - (a.total_quantity ?? 0) || byTitle(a, b);
        case 'regular_asc':
          return (a.regular_quantity ?? 0) - (b.regular_quantity ?? 0) || (a.total_quantity ?? 0) - (b.total_quantity ?? 0) || byTitle(a, b);
        case 'title_asc':
          return byTitle(a, b);
        case 'title_desc':
          return byTitle(b, a);
        case 'total_desc':
        default:
          return (b.total_quantity ?? 0) - (a.total_quantity ?? 0) || byTitle(a, b);
      }
    });

    return rows;
  }, [data.rows, sortBy]);

  const filteredSortedDaily = useMemo(() => {
    const rows = Array.isArray(data.daily) ? [...data.daily] : [];
    const withStatus = rows.map((d) => {
      const p = Number(d.preorder_qty ?? 0);
      const r = Number(d.regular_qty ?? 0);
      let rowStatus = 'none';
      if (p > 0 && r > 0) rowStatus = 'mixed';
      else if (p > 0) rowStatus = 'preorder';
      else if (r > 0) rowStatus = 'regular';
      return { ...d, rowStatus };
    });

    const filtered = withStatus.filter((d) => dayStatusFilter === 'all' || d.rowStatus === dayStatusFilter);

    filtered.sort((a, b) => {
      switch (daySortBy) {
        case 'date_asc':
          return String(a.date || '').localeCompare(String(b.date || ''));
        case 'total_desc':
          return (b.total ?? 0) - (a.total ?? 0) || String(a.date || '').localeCompare(String(b.date || ''));
        case 'total_asc':
          return (a.total ?? 0) - (b.total ?? 0) || String(a.date || '').localeCompare(String(b.date || ''));
        case 'preorder_desc':
          return (b.preorder_qty ?? 0) - (a.preorder_qty ?? 0) || (b.total ?? 0) - (a.total ?? 0);
        case 'preorder_asc':
          return (a.preorder_qty ?? 0) - (b.preorder_qty ?? 0) || (a.total ?? 0) - (b.total ?? 0);
        case 'regular_desc':
          return (b.regular_qty ?? 0) - (a.regular_qty ?? 0) || (b.total ?? 0) - (a.total ?? 0);
        case 'regular_asc':
          return (a.regular_qty ?? 0) - (b.regular_qty ?? 0) || (a.total ?? 0) - (b.total ?? 0);
        case 'date_desc':
        default:
          return String(b.date || '').localeCompare(String(a.date || ''));
      }
    });

    return filtered;
  }, [data.daily, daySortBy, dayStatusFilter]);

  return (
    <div className="sales-report-page">
      <div className="sales-report-hero">
        <h1>Book sales</h1>
        <p className="sales-report-lead">
          Quantity sold per product: preorder vs regular (regular = orders that were never marked preorder at
          checkout). Dates use order time in Asia/Kolkata.
        </p>
      </div>

      <div className="sales-report-filters card">
        <div className="sales-report-filters-grid">
          <div className="sr-scope-full">
            <span className="sr-label-text">Order Scope</span>
            <div className="sr-scope-switcher" role="tablist" aria-label="Order scope filters">
              <button
                type="button"
                role="tab"
                aria-selected={orderScope === 'all'}
                className={`sr-scope-pill${orderScope === 'all' ? ' active' : ''}`}
                onClick={() => setOrderScope('all')}
              >
                <span className="sr-scope-dot all" />
                All
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={orderScope === 'shipping'}
                className={`sr-scope-pill${orderScope === 'shipping' ? ' active' : ''}`}
                onClick={() => setOrderScope('shipping')}
              >
                <span className="sr-scope-dot shipping" />
                Shipping Required
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={orderScope === 'digital'}
                className={`sr-scope-pill${orderScope === 'digital' ? ' active' : ''}`}
                onClick={() => setOrderScope('digital')}
              >
                <span className="sr-scope-dot digital" />
                Non Shipping
              </button>
            </div>
          </div>

          <div className="sr-date-range-full">
            <span className="sr-label-text">Date range</span>
            <div className="sr-date-tabs" role="tablist" aria-label="Date range filters">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  role="tab"
                  aria-selected={datePreset === p.value}
                  className={`sr-date-tab${datePreset === p.value ? ' active' : ''}`}
                  onClick={() => handlePresetChange({ target: { value: p.value } })}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {datePreset === 'custom' ? (
            <>
              <label className="sr-label">
                From
                <input
                  type="date"
                  value={dateFrom}
                  onChange={handleCustomFromChange}
                  max={dateTo}
                />
              </label>
              <label className="sr-label">
                To
                <input
                  type="date"
                  value={dateTo}
                  onChange={handleCustomToChange}
                  min={dateFrom}
                />
              </label>
            </>
          ) : null}

          <div className="sr-product-full" ref={comboboxRef}>
            <span className="sr-label-text">Product</span>
            <div className="sr-combobox">
              <input
                type="text"
                className="sr-combobox-input"
                placeholder={
                  productsLoading
                    ? 'Loading products…'
                    : 'Type to search, then pick a product or click Apply…'
                }
                value={search}
                disabled={productsLoading}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setListOpen(true);
                }}
                onFocus={() => setListOpen(true)}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={listOpen}
              />
              {listOpen && !productsLoading ? (
                <ul className="sr-combobox-list" role="listbox">
                  <li
                    role="option"
                    className="sr-combobox-item sr-combobox-item-all"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickProduct('')}
                  >
                    — All products —
                  </li>
                  {listSlice.length === 0 ? (
                    <li className="sr-combobox-item muted">No product matches</li>
                  ) : (
                    listSlice.map((p) => (
                      <li
                        key={p.id}
                        role="option"
                        className="sr-combobox-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickProduct(p.title)}
                      >
                        {p.title}
                      </li>
                    ))
                  )}
                  {filteredProducts.length > LIST_MAX ? (
                    <li className="sr-combobox-item muted sr-combobox-hint">
                      Showing first {LIST_MAX} matches — keep typing to narrow
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="sr-footer-row">
            <div className="sales-report-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApply}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
        {error ? <div className="sales-report-error">{error}</div> : null}
      </div>

      <div className="sales-report-stats">
        <div className="stat-pill orders">
          <span className="stat-pill-label">Orders</span>
          <span className="stat-pill-value">{t.order_count ?? 0}</span>
        </div>
        <div className="stat-pill preorder">
          <span className="stat-pill-label">Preorder copies</span>
          <span className="stat-pill-value">{t.preorder_quantity ?? 0}</span>
        </div>
        <div className="stat-pill regular">
          <span className="stat-pill-label">Regular copies</span>
          <span className="stat-pill-value">{t.regular_quantity ?? 0}</span>
        </div>
        <div className="stat-pill total">
          <span className="stat-pill-label">Total copies</span>
          <span className="stat-pill-value">{t.total_quantity ?? 0}</span>
        </div>
      </div>

      <div className="card sales-report-table-card">
        <div className="card-header">
          <div className="sr-table-header-row">
            <div>
              <div className="sr-tabs" role="tablist" aria-label="Sales breakdown tabs">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'product'}
                  className={`sr-tab-btn${activeTab === 'product' ? ' active' : ''}`}
                  onClick={() => setActiveTab('product')}
                >
                  By product
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'day'}
                  className={`sr-tab-btn${activeTab === 'day' ? ' active' : ''}`}
                  onClick={() => setActiveTab('day')}
                >
                  By day
                </button>
              </div>
              {rangeHint ? (
                <p
                  className="sales-report-range-hint"
                  title="Date range preset ya Custom From/To se — Asia/Kolkata order dates."
                >
                  {rangeHint.days} day{rangeHint.days === 1 ? '' : 's'} of data · {rangeHint.fromLabel} –{' '}
                  {rangeHint.toLabel}
                </p>
              ) : null}
            </div>
            {activeTab === 'product' ? (
              <label className="sr-sort">
                <span className="sr-sort-label">Sort</span>
                <select
                  className="sr-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort by"
                >
                  <option value="total_desc">Top sell (Total high → low)</option>
                  <option value="total_asc">Low sale (Total low → high)</option>
                  <option value="release_desc">New release → Old</option>
                  <option value="release_asc">Old release → New</option>
                  <option value="preorder_desc">Preorder high → low</option>
                  <option value="preorder_asc">Preorder low → high</option>
                  <option value="regular_desc">Regular high → low</option>
                  <option value="regular_asc">Regular low → high</option>
                  <option value="title_asc">Title A → Z</option>
                  <option value="title_desc">Title Z → A</option>
                </select>
              </label>
            ) : (
              <div className="sr-day-filters">
                <label className="sr-sort">
                  <span className="sr-sort-label">Sort</span>
                  <select
                    className="sr-sort-select"
                    value={daySortBy}
                    onChange={(e) => setDaySortBy(e.target.value)}
                    aria-label="By day sort"
                  >
                    <option value="date_desc">Date new → old</option>
                    <option value="date_asc">Date old → new</option>
                    <option value="total_desc">Top total → low</option>
                    <option value="total_asc">Low total → high</option>
                    <option value="preorder_desc">Preorder high → low</option>
                    <option value="preorder_asc">Preorder low → high</option>
                    <option value="regular_desc">Regular high → low</option>
                    <option value="regular_asc">Regular low → high</option>
                  </select>
                </label>
                <label className="sr-sort">
                  <span className="sr-sort-label">Status</span>
                  <select
                    className="sr-sort-select"
                    value={dayStatusFilter}
                    onChange={(e) => setDayStatusFilter(e.target.value)}
                    aria-label="By day status filter"
                  >
                    <option value="all">All status</option>
                    <option value="preorder">Preorder only</option>
                    <option value="regular">Regular only</option>
                    <option value="mixed">Mixed (preorder + regular)</option>
                    <option value="none">No qty rows</option>
                  </select>
                </label>
              </div>
            )}
          </div>
        </div>
        {activeTab === 'product' ? (
          <div className="table-wrap">
            <table className="sales-report-table">
              <thead>
                <tr>
                  <th>Book / product</th>
                  <th
                    className="sr-col-released"
                    title="Kab live / release hua: Shopify published date (pehle), warna product created date. Store timezone: Asia/Kolkata."
                  >
                    Book released
                  </th>
                  <th className="num">Preorder copies</th>
                  <th className="num">Regular copies</th>
                  <th className="num">Total copies</th>
                  <th className="num">Orders</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      Loading…
                    </td>
                  </tr>
                ) : data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      No line items in this range (or search has no matches).
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((r, i) => (
                    <tr key={`${r.title}-${i}`}>
                      <td>{r.title}</td>
                      <td
                        className="sr-col-released"
                        title={
                          r.release_source === 'published'
                            ? 'Published / live date (Shopify)'
                            : r.release_source === 'created'
                              ? 'Product created in Shopify (no publish date)'
                              : 'Not in local catalog or no date'
                        }
                      >
                        {r.book_released_at ? formatShortDate(r.book_released_at) : '—'}
                      </td>
                      <td className="num">{r.preorder_quantity}</td>
                      <td className="num">{r.regular_quantity}</td>
                      <td className="num strong">{r.total_quantity}</td>
                      <td className="num">{r.order_count ?? 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrap table-wrap-scroll">
            <table className="sales-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Book / product</th>
                  <th className="sr-col-released" title="Same as By product — book go-live / release date">
                    Book released
                  </th>
                  <th className="num">Preorder copies</th>
                  <th className="num">Regular copies</th>
                  <th className="num">Total copies</th>
                  <th className="num">Orders</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      Loading…
                    </td>
                  </tr>
                ) : filteredSortedDaily.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      No day-wise rows for selected filter.
                    </td>
                  </tr>
                ) : (
                  filteredSortedDaily.map((d, i) => (
                    <tr key={`${d.date}-${d.title}-${i}`}>
                      <td>{d.date}</td>
                      <td>{d.title}</td>
                      <td className="sr-col-released">
                        {d.book_released_at ? formatShortDate(d.book_released_at) : '—'}
                      </td>
                      <td className="num">{d.preorder_qty}</td>
                      <td className="num">{d.regular_qty}</td>
                      <td className="num">{d.total}</td>
                      <td className="num">{d.order_count ?? 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesReport;
